import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex w-64 shrink-0 items-center gap-2" }
const _hoisted_2 = { class: "shrink-0 text-xs font-medium text-gray-600" }
const _hoisted_3 = { class: "truncate" }
const _hoisted_4 = ["title"]
const _hoisted_5 = ["title"]
const _hoisted_6 = {
  key: 1,
  class: "inline-flex min-w-0 flex-1 items-center gap-1 text-xs italic text-gray-400"
}
const _hoisted_7 = { class: "truncate" }
const _hoisted_8 = ["title"]

import { computed } from 'vue';
import { Info } from 'lucide-vue-next';
import AutoComplete from '@/components/ui/AutoComplete.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import type { ComparisonCandidate } from './productivityComparison';


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductivityComparisonPicker',
  props: {
    candidates: {},
    selected: {},
    unavailableReason: {}
  },
  emits: ["reference-picked"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Whether the pick is being kept rather than drawn - the range covers several Production Groups, so the
// control is unavailable while the selection itself still stands.
const held = computed(() => props.selected !== null && props.unavailableReason !== null);

// Why the chip is muted: the reason the control is unavailable, plus what is being done about the
// pick. The first half alone would leave a reader wondering whether their reference had been thrown
// away, which is the question a greyed-out chip with an X on it actually raises.
const heldReason = computed(
    () => `${props.unavailableReason} ${i18n.global.t('views.monitoring.activeGroups.modals.productivity.compareHeldReason')}`
);

// The reason the picker is not offered: the range spanning several Production Groups takes precedence
// over the Section simply having nothing to compare against, since it is the narrower explanation.
const disabledReason = computed(
    () => props.unavailableReason ?? (i18n.global.t('views.monitoring.activeGroups.modals.productivity.compareEmptyState') as string)
);

// The few words that fit on the toolbar; disabledReason above is the sentence behind the icon.
const disabledSummary = computed(() =>
    props.unavailableReason !== null
        ? (i18n.global.t('views.monitoring.activeGroups.modals.productivity.compareUnavailableShort') as string)
        : (i18n.global.t('views.monitoring.activeGroups.modals.productivity.compareEmptyShort') as string)
);

function onItemClicked(item: ComparisonCandidate): void {
    emit('reference-picked', item);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("span", _hoisted_2, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.productivity.compareHeading')), 1),
    (_ctx.selected)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["flex min-w-0 items-center gap-1 rounded-full px-2 py-1 text-xs", held.value ? 'bg-gray-100 text-gray-500' : 'bg-primary-300 text-primary-800'])
        }, [
          _createElementVNode("span", _hoisted_3, _toDisplayString(_ctx.selected.name), 1),
          (held.value)
            ? (_openBlock(), _createElementBlock("span", {
                key: 0,
                class: "inline-flex shrink-0 cursor-help",
                title: heldReason.value
              }, [
                _createVNode(_unref(Info), {
                  class: "h-3.5 w-3.5",
                  "aria-hidden": "true"
                })
              ], 8, _hoisted_4))
            : _createCommentVNode("", true),
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass(["shrink-0", held.value ? 'text-gray-400 hover:text-gray-600' : 'text-primary-700 hover:text-primary-800']),
            title: _ctx.$t('views.monitoring.activeGroups.modals.productivity.compareClear'),
            onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('reference-picked', null)))
          }, " × ", 10, _hoisted_5)
        ], 2))
      : (_ctx.unavailableReason || _ctx.candidates.length === 0)
        ? (_openBlock(), _createElementBlock("span", _hoisted_6, [
            _createElementVNode("span", _hoisted_7, _toDisplayString(disabledSummary.value), 1),
            _createElementVNode("span", {
              class: "inline-flex shrink-0 cursor-help not-italic",
              title: disabledReason.value
            }, [
              _createVNode(_unref(Info), {
                class: "h-3.5 w-3.5",
                "aria-hidden": "true"
              })
            ], 8, _hoisted_8)
          ]))
        : (_openBlock(), _createBlock(AutoComplete, {
            key: 2,
            class: "min-w-0 flex-1",
            items: _ctx.candidates,
            "item-text": "name",
            "item-value": "productionGroupId",
            label: _ctx.$t('views.monitoring.activeGroups.modals.productivity.comparePickerLabel'),
            onItemClicked: onItemClicked
          }, null, 8, ["items", "label"]))
  ]))
}
}

})